import React, { useState } from 'react'
import styles from './media-card.module.css'
import { MediaItem } from '../../types'
import { getEmbedSrc } from '../../utils/media-utils'

interface MediaCardProps {
  item: MediaItem
  onPlay?: (item: MediaItem) => void
}

export default function MediaCard({ item, onPlay }: MediaCardProps) {
  const [showEmbed, setShowEmbed] = useState(false);
  const typeName = item.type === 'podcast_video' ? 'Podcast & Video' : item.type;
  const outletName = typeof item.outlet === 'string' ? item.outlet : item.outlet?.name;
  const outletLogoUrl = typeof item.outlet === 'string' ? undefined : item.outlet?.logoUrl;
  const outletOrType = outletName || typeName;

  const trackEvent = (eventName: string, additionalParams: Record<string, any> = {}) => {
    window.analytics?.trackEvent(eventName, {
      media_id: item.a_id || item.id,
      media_title: item.title,
      media_type: item.type,
      media_outlet: outletName,
      media_url: item.linkUrl,
      media_location: 'media_page',
      ...additionalParams
    });
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault()
    trackEvent('media_watch', {
      method: onPlay ? 'embed_play_overlay' : 'embed_play'
    });
    if (onPlay) {
      onPlay(item);
    } else {
      setShowEmbed(true);
    }
  }

  const handleThumbnailClick = (e: React.MouseEvent) => {
    if (item.embedUrl) {
      handlePlayClick(e);
    } else {
      handleReadClick();
    }
  };

  // When we have an embed, keep user on page: thumbnail and Watch action stay in-app
  const thumbnailHref = item.embedUrl ? '#' : item.linkUrl;
  const thumbnailTarget = item.embedUrl ? undefined : '_blank';
  const thumbnailRel = item.embedUrl ? undefined : 'noopener noreferrer';

  const handleWatchClick = () => {
    trackEvent('media_watch', {
      method: 'external_link',
      url: item.videoUrl || item.linkUrl
    });
  };

  const handleListenClick = () => {
    trackEvent('media_listen', {
      url: item.audioUrl || item.linkUrl
    });
  };

  const handleReadClick = () => {
    trackEvent('media_click', {
      url: item.linkUrl
    });
  };

  // Format date safely avoiding timezone shifts
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    // Create date at noon to avoid any midnight shifts
    const date = new Date(year, month - 1, day, 12);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  const formattedDate = formatDate(item.date);

  return (
    <div className={styles.card}>
      <div className={styles.mediaContainer}>
        {showEmbed && item.embedUrl ? (
          <iframe
            src={getEmbedSrc(item.embedUrl)}
            title={item.title}
            className={styles.iframe}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        ) : (
          <a
            href={thumbnailHref}
            {...(thumbnailTarget ? { target: thumbnailTarget, rel: thumbnailRel } : {})}
            className={styles.imageLink}
            onClick={handleThumbnailClick}
          >
            <img
              src={item.imageUrl}
              alt={item.title}
              className={styles.image}
            />
            {item.embedUrl && (
              <div className={styles.playOverlay}>
                <div className={styles.playButton}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className={styles.playIcon}
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
            {outletLogoUrl && outletName && (
              <div className={styles.outletOverlay}>
                <img src={outletLogoUrl} alt={outletName} className={styles.outletLogo} />
              </div>
            )}
          </a>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.meta}>
          <span className={styles.outlet}>
            {outletOrType}
          </span>
          <span className={styles.date}>{formattedDate}</span>
        </div>
        <h3 className={styles.title}>
          <a
            href={item.embedUrl ? '#' : item.linkUrl}
            {...(item.embedUrl ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
            className={styles.titleLink}
            onClick={item.embedUrl
              ? (e: React.MouseEvent) => {
                  e.preventDefault();
                  handlePlayClick(e);
                }
              : () => handleReadClick()
            }
          >
            {item.title}
          </a>
        </h3>
        {item.description && (
          <p className={styles.description}>
            {item.description}
          </p>
        )}
        <div className={styles.footer}>
          {item.type === 'podcast_video' && (item.videoUrl || item.linkUrl) ? (
            <div className={styles.dualActions}>
              {item.embedUrl ? (
                <button
                  type="button"
                  className={styles.readMore}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePlayClick(e);
                  }}
                >
                  Watch
                  <svg
                    className={styles.arrowIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </button>
              ) : (
                <a
                  href={item.videoUrl || item.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.readMore}
                  onClick={handleWatchClick}
                >
                  Watch
                  <svg
                    className={styles.arrowIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </a>
              )}
              <a
                href={item.audioUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.secondaryAction}
                onClick={handleListenClick}
              >
                Listen
              </a>
            </div>
          ) : (
            (item.type === 'video' || item.type === 'podcast_video') && item.embedUrl ? (
              <button
                type="button"
                className={styles.readMore}
                onClick={(e) => {
                  e.preventDefault();
                  handlePlayClick(e);
                }}
              >
                Watch
                <svg
                  className={styles.arrowIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </button>
            ) : (
              <a
                href={item.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.readMore}
                onClick={(e) => {
                  if (item.type === 'video' || item.type === 'podcast_video') {
                    handleWatchClick();
                  } else if (item.type === 'podcast') {
                    handleListenClick();
                  } else {
                    handleReadClick();
                  }
                }}
              >
                {item.type === 'video' || item.type === 'podcast_video' ? 'Watch' : item.type === 'podcast' ? 'Listen' : 'Read'}
                <svg
                  className={styles.arrowIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </a>
            )
          )}
        </div>
      </div>
    </div>
  )
}
